import { MessagingDock } from "../components/MessagingDock";
import { PresenceHeartbeat } from "../hooks/usePresenceHeartbeat";
import { useState } from 'react';
import { AdminMfaCancelledError, resetAdminMfaCancellation } from '../api/admin-mfa';
import { RdvConnectionPrompt } from '../components/RdvConnectionPrompt';
import { LegalProfileBanner } from '../components/LegalProfileBanner';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { Sidebar } from '../components/Sidebar';
import { TopBar } from '../components/TopBar';
import { billingApi } from '../api/billing.api';
import { schoolApi } from '../api/school.api';
import { SubscriptionContext, SubscriptionPlan } from '../types';
import { Navigate } from 'react-router-dom';
import { LoadingScreen } from '../components/LoadingScreen';
import { TermsGate } from '../components/TermsGate';
import { legalTermsApi } from '../api/legal-terms.api';

// Seul /checkout se rend sans sidebar (page Stripe plein écran)
const CHECKOUT_PATHS = ['/checkout'];

// Pages déjà migrées au design v2 : elles intègrent leur propre en-tête
// (breadcrumb + titre) et leur fond, donc on masque la TopBar globale et on
// retire le padding de `.app-shell__main`. À compléter au fil des migrations.
// Correspondance EXACTE : on ne veut pas masquer la TopBar sur les sous-routes
// non encore migrées (ex. /requests/new, /requests/:id/edit).
const V2_MIGRATED_PATHS = [
  '/today',
  '/requests',
  '/candidates',
  '/planning',
  '/lessons',
  '/slots',
  '/exams',
  '/exams/preparation',
  '/exams/forecasts',
  '/boutique-permio',
  '/exams/student-requests',
  '/instructors',
  '/statistics',
  '/fleet',
  '/compte',
  '/network',
  '/catalog',
  '/reglages',
  '/fleet/availability',
  '/fleet/candidate-availability',
  '/payments',
  '/notifications',
  '/etps',
  '/windows',
  '/contracts',
  '/invoices',
  '/quotes',
  '/orders',
  '/livret',
  '/settings',
  '/settings/ai',
  '/profile',
  '/account-settings',
  '/security',
  '/subscriptions',
  '/messages',
  '/workspace/tasks',
  '/workspace/meetings',
  '/workspace/notes',
  '/workspace/boards',
  '/workspace/sequences',
];

// Préfixes dont TOUTES les sous-routes sont migrées (page liste + formulaires) :
// on peut masquer la TopBar sur l'ensemble du préfixe (ex. /contracts/new,
// /invoices/:id/edit). Les préfixes non listés restent en correspondance exacte
// pour ne pas affecter des sous-routes encore en ancien design.
const V2_MIGRATED_PREFIXES = ['/contracts', '/invoices', '/quotes', '/orders', '/requests', '/candidates', '/reglages'];

// ── Écran paywall plein écran ─────────────────────────────────────────────────

function formatAmount(plan: SubscriptionPlan) {
  if (!plan.amountCents) return 'Sur devis';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: (plan.currency ?? 'EUR').toUpperCase(),
    maximumFractionDigits: 0,
  }).format(plan.amountCents / 100);
}

/** Refus de souscrire expliqué par le serveur (409 : abonnement déjà en place), sinon le texte générique. */
function checkoutRefusal(error: unknown) {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } }).response;
  return response?.status === 409 && typeof response.data?.message === 'string'
    ? response.data.message
    : 'Impossible d’ouvrir le paiement. Réessayez dans quelques instants.';
}

function PaywallScreen({ subContext }: { subContext: SubscriptionContext }) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const { school } = useAuthStore((s) => ({ school: s.school }));
  // Souscrire ou mettre à jour le paiement engage l’auto-école : le serveur le réserve au gérant.
  const canSubscribe = ['owner', 'super_admin'].includes(useAuthStore((s) => s.user?.role ?? ''));
  // Paiement en échec : l'abonnement existe, le serveur refuse d'en souscrire un second (409). Le
  // gérant met à jour son moyen de paiement ; l'accès revient quand la facture est réglée.
  const pastDue = subContext.subscriptionStatus === 'past_due';
  const canCheckout = canSubscribe && !pastDue;

  const checkoutMutation = useMutation({
    mutationFn: (plan: SubscriptionPlan) =>
      billingApi.createCheckoutSession({ priceId: plan.priceId }),
    onSuccess: (session, plan) => {
      const resolvedPlan = session.plan ?? plan;
      navigate('/checkout', {
        state: {
          clientSecret: session.clientSecret,
          plan: resolvedPlan,
          publishableKey: subContext.publishableKey ?? '',
        },
      });
    },
  });
  const portalMutation = useMutation({
    mutationFn: () => billingApi.createPortalSession({ returnUrl: `${window.location.origin}/reglages/abonnement` }),
    onSuccess: ({ url }) => window.location.assign(url),
  });

  const effectiveRecommendedId =
    subContext.recommendedPriceId ??
    subContext.plans.find((p) => p.highlighted)?.priceId ??
    null;

  const isEtpFeature = (f: string) => /\betps?\b/i.test(f);

  const allFeatures = Array.from(
    new Set(subContext.plans.flatMap((p) => (p.features ?? []).filter((f) => !isEtpFeature(f))))
  );

  const ETP_LABELS: Record<number, string> = {
    1: "Jusqu'à 3",
    2: '3 à 7',
    3: '7 à 12',
  };

  const getEtpValue = (plan: SubscriptionPlan): string => {
    if (plan.tier === 'full') return '∞';
    const tier = plan.etpTier;
    if (!tier) return '—';
    return ETP_LABELS[tier] ?? '—';
  };

  const getCumulativeFeatures = (planIndex: number): Set<string> => {
    const result = new Set<string>();
    for (let i = 0; i <= planIndex; i++) {
      (subContext.plans[i].features ?? []).forEach((f) => result.add(f));
    }
    return result;
  };


  return (
    <div className="paywall-screen">
      <div className="paywall-screen__brand">
        <img src="/logo-permio-halloween.png" alt="Permio" className="sidebar__logo" />
        {school?.name && <span className="paywall-screen__school">{school.name}</span>}
      </div>

      <div className="paywall-screen__card paywall-screen__card--wide">
        <div className="paywall-screen__icon">🔒</div>
        <h1 className="paywall-screen__title">Abonnement requis</h1>
        <p className="paywall-screen__subtitle">
          {pastDue
            ? 'Le dernier paiement de votre abonnement Permio a échoué. Mettez à jour le moyen de paiement pour rétablir l’accès.'
            : 'Abonnez-vous à Permio pour accéder à votre espace.'}
        </p>
        {!canSubscribe && (
          <p className="paywall-screen__subtitle" role="note">
            Seul le gérant de l’auto-école peut souscrire ou mettre à jour le moyen de paiement. Prévenez-le pour rétablir l’accès.
          </p>
        )}
        {canSubscribe && pastDue && <button className="btn btn--primary" onClick={() => portalMutation.mutate()} disabled={portalMutation.isPending}>Mettre à jour mon moyen de paiement</button>}
        {portalMutation.isError && <p role="alert">Impossible d’ouvrir la gestion de l’abonnement. Réessayez.</p>}

        {subContext.plans.length > 1 && subContext.etpSummary && (
          <div className="paywall-screen__etp-banner">
            <span className="paywall-screen__etp-banner-icon">📊</span>
            <span>
              Votre auto-école&nbsp;:&nbsp;
              <strong>{subContext.etpSummary.totalEtp} ETP au total</strong>
            </span>
          </div>
        )}

        {subContext.plans.length === 1 && subContext.plans.map((plan) => (
          <div key={plan.priceId}>
            <h2>{plan.label}</h2>
            <p>{formatAmount(plan)}{plan.interval ? ` / ${plan.interval === 'month' ? 'mois' : 'an'}` : ''}</p>
            <p>Accès à l’ensemble des fonctionnalités Permio.</p>
            {canCheckout && (
              <button className="btn btn--primary" onClick={() => checkoutMutation.mutate(plan)} disabled={checkoutMutation.isPending}>
                {checkoutMutation.isPending ? 'Chargement…' : 'Souscrire'}
              </button>
            )}
          </div>
        ))}
        {subContext.plans.length > 1 && (
          <div className="paywall-table-wrap">
            <table className="paywall-table">
              <thead>
                <tr>
                  <th className="paywall-table__feature-header" />
                  {subContext.plans.map((plan) => {
                    const isPremium = plan.tier === 'full';
                    const isRecommended = !isPremium && plan.priceId === effectiveRecommendedId;
                    return (
                    <th
                      key={plan.priceId}
                      className={[
                        'paywall-table__plan-header',
                        isPremium ? 'paywall-table__plan-header--premium' : '',
                        isRecommended ? 'paywall-table__plan-header--highlight' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {/* Slot de hauteur fixe pour les badges — garde tous les headers alignés */}
                      <div className="paywall-table__badge-slot">
                        {isPremium && (
                          <span className="paywall-table__badge paywall-table__badge--premium">✦ Premium</span>
                        )}
                        {isRecommended && (
                          <span className="paywall-table__badge">Recommandé</span>
                        )}
                      </div>
                      <div className="paywall-table__plan-name">{plan.label}</div>
                      <div className="paywall-table__plan-price">
                        <span className={`paywall-table__price-amount${isPremium ? ' paywall-table__price-amount--premium' : ''}`}>
                          {formatAmount(plan)}
                        </span>
                        {plan.interval && (
                          <span className="paywall-table__price-interval">
                            /{plan.interval === 'month' ? 'mois' : 'an'}
                          </span>
                        )}
                      </div>
                      {canCheckout && (
                        <button
                          className={`btn paywall-table__cta${isPremium ? ' paywall-table__cta--premium' : isRecommended ? ' btn--primary' : ' btn--outline'}`}
                          onClick={() => checkoutMutation.mutate(plan)}
                          disabled={checkoutMutation.isPending}
                        >
                          {checkoutMutation.isPending ? 'Chargement…' : 'Souscrire'}
                        </button>
                      )}
                    </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                  <tr className="paywall-table__row">
                    <td className="paywall-table__feature-name">ETP inclus</td>
                    {subContext.plans.map((plan) => {
                      const isPremium = plan.tier === 'full';
                      const isRecommended = !isPremium && plan.priceId === effectiveRecommendedId;
                      return (
                        <td
                          key={plan.priceId}
                          className={[
                            'paywall-table__check paywall-table__check--etp',
                            isPremium ? 'paywall-table__check--premium-col' : '',
                            isRecommended ? 'paywall-table__check--highlight-col' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <span className={`paywall-table__etp-value${isPremium ? ' paywall-table__etp-value--premium' : ''}`}>
                            {getEtpValue(plan)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                  {allFeatures.map((feature, i) => (
                    <tr key={i} className="paywall-table__row">
                      <td className="paywall-table__feature-name">{feature}</td>
                      {subContext.plans.map((plan, planIndex) => {
                        const isPremium = plan.tier === 'full';
                        const isRecommended = !isPremium && plan.priceId === effectiveRecommendedId;
                        const included = isPremium || getCumulativeFeatures(planIndex).has(feature);
                        return (
                          <td
                            key={plan.priceId}
                            className={[
                              'paywall-table__check',
                              included ? 'paywall-table__check--yes' : 'paywall-table__check--no',
                              isPremium ? 'paywall-table__check--premium-col' : '',
                              isRecommended ? 'paywall-table__check--highlight-col' : '',
                            ].filter(Boolean).join(' ')}
                          >
                            {included ? (
                              <span className="paywall-table__icon paywall-table__icon--yes">✓</span>
                            ) : (
                              <span className="paywall-table__icon paywall-table__icon--no">✕</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
            </table>
            <p className="paywall-table__hors-forfait-note">
              * Au-delà du palier 3, chaque ETP supplémentaire fait l’objet d’un devis, au tarif indicatif de&nbsp;
              <strong>
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }).format((subContext.horsForfaitPerEtpCents ?? 700) / 100)} TTC
              </strong>
              &nbsp;/ mois.
            </p>
          </div>
        )}

        {checkoutMutation.isError && <p role="alert">{checkoutRefusal(checkoutMutation.error)}</p>}
        {canCheckout && subContext.checkoutUrl && subContext.plans.length === 0 && (
          <a href={subContext.checkoutUrl} className="btn btn--primary paywall-screen__cta">
            Voir les offres
          </a>
        )}
      </div>

      <button
        className="paywall-screen__logout"
        onClick={() => logout()}
      >
        Se déconnecter
      </button>
    </div>
  );
}

// ── Layout principal ──────────────────────────────────────────────────────────

export function ProtectedLayout() {
  const { token, isBootstrapping, activeSchoolId, user, logout, schools } = useAuthStore((state) => ({
    token: state.token,
    user: state.user,
    isBootstrapping: state.isBootstrapping,
    activeSchoolId: state.activeSchoolId,
    logout: state.logout,
    schools: state.availableSchools,
  }));
  // Conditions en vigueur à accepter avant tout usage (CGU, accord de traitement pour le gérant).
  const terms = useQuery({
    queryKey: ['legal-terms', activeSchoolId, user?.id],
    queryFn: () => legalTermsApi.status(),
    enabled: !!token && !!activeSchoolId && user?.role !== 'super_admin',
    staleTime: 5 * 60_000,
    // Une seule nouvelle tentative, rapide : en cas d'échec, l'accès n'est pas retardé ni bloqué.
    retry: (count, error) => !(error instanceof AdminMfaCancelledError) && count < 1,
    retryDelay: 300,
  });
  const location = useLocation();
  const rdvCallbackFailed = new URLSearchParams(location.search).get('rdvOauthStatus') === 'error';
  const [dismissedSchool, setDismissedSchool] = useState<string | null>(null);

  const { data: subContext, isLoading: isSubContextLoading, isError: subscriptionError, error: subscriptionFailure, refetch: reloadSubscription } = useQuery({
    queryKey: ['subscription-context', activeSchoolId, user?.id],
    queryFn: () => billingApi.fetchSubscriptionContext(),
    enabled: !!token && !!activeSchoolId,
    staleTime: 0,
    retry: (count, error) => !(error instanceof AdminMfaCancelledError) && count < 3,
    refetchInterval: (query) => query.state.status === 'error' || query.state.data?.hasActiveSubscription || query.state.data?.subscriptionExempt ? false : 10000,
  });

  const { data: settingsData, isLoading: rdvStatusLoading } = useQuery({
    queryKey: ['school-settings-rdv-status', activeSchoolId, user?.id],
    queryFn: () => schoolApi.fetchSettings(),
    enabled: !!token && !!activeSchoolId && !!subContext && !subscriptionError && (!subContext.subscriptionEnabled || subContext.hasActiveSubscription || subContext.subscriptionExempt === true),
    staleTime: 0,
    select: (d) => d.credential,
  });

  const reconnectMutation = useMutation({
    mutationFn: () => schoolApi.startRdvPermisOauth({ redirectUri: `${window.location.origin}/requests` }),
    onSuccess: (data) => {
      if (data?.authorizationUrl) {
        window.location.href = data.authorizationUrl;
      }
    },
  });

  const rdvReconnectRequired = ['owner', 'manager'].includes(user?.role ?? '') && settingsData?.status === 'reconnect_required';

  if (isBootstrapping) return <LoadingScreen message="Connexion à votre espace…" />;

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const onCheckoutPath = CHECKOUT_PATHS.includes(location.pathname);

  if (terms.isLoading) return <LoadingScreen message="Chargement..." />;
  if (terms.data?.required)
    return (
      <TermsGate
        status={terms.data}
        schoolName={schools.find((school) => school.id === activeSchoolId)?.name}
        onLogout={() => void logout()}
      />
    );

  // Bloquer le rendu jusqu'à ce qu'on sache si l'utilisateur est abonné,
  // pour éviter le flash de l'application avant la redirection vers le paywall.
  if (!onCheckoutPath && !!token && !!activeSchoolId && isSubContextLoading) {
    return <LoadingScreen message="Chargement..." />;
  }

  if (!onCheckoutPath && subscriptionError) {
    return <div className="paywall-screen"><div className="paywall-screen__card">
      <h1 className="paywall-screen__title">{subscriptionFailure instanceof AdminMfaCancelledError ? 'Vérification de connexion annulée' : 'Vérification de l’abonnement indisponible'}</h1>
      <p className="paywall-screen__subtitle">Réessayez pour accéder à votre espace.</p>
      <div className="paywall-screen__actions">
      <button className="btn btn--primary" onClick={() => { resetAdminMfaCancellation(); void reloadSubscription(); }}>Réessayer</button>
      <button className="paywall-screen__logout" onClick={() => void useAuthStore.getState().logout()}>Se déconnecter</button>
      </div>
    </div></div>;
  }
  if (!onCheckoutPath && subContext?.subscriptionEnabled && !subContext.hasActiveSubscription && !subContext.subscriptionExempt) {
    return <PaywallScreen subContext={subContext} />;
  }

  // Page checkout Stripe : sans sidebar
  if (onCheckoutPath) {
    return (
      <main style={{ minHeight: '100vh' }}>
        <Outlet />
      </main>
    );
  }

  // Invite « Connecter RdvPermis » : sur Réservation d’examen (ex-Vue d'ensemble, où arrivent les retours de paiement).
  if (location.pathname === '/requests' && ['owner', 'manager'].includes(user?.role ?? '') && rdvStatusLoading)
    return <LoadingScreen message="Préparation de votre espace…" />;

  if (activeSchoolId && settingsData && !settingsData.hasCredential &&
      ['owner', 'manager'].includes(user?.role ?? '') && dismissedSchool !== activeSchoolId &&
      location.pathname === '/requests') {
    return <RdvConnectionPrompt callbackFailed={rdvCallbackFailed} onLater={() => setDismissedSchool(activeSchoolId)} />;
  }

  const showPastDueBanner = subContext?.subscriptionStatus === 'past_due';
  const isV2Page =
    V2_MIGRATED_PATHS.includes(location.pathname) ||
    V2_MIGRATED_PREFIXES.some((p) => location.pathname.startsWith(`${p}/`));

  return (
    <div className="app-shell">
      <Sidebar subContext={subContext} />
      <MessagingDock key={user?.id} />
      <PresenceHeartbeat key={`presence-${user?.id}`} />
      <div className="app-shell__content">
        {!isV2Page && <TopBar />}
        {rdvCallbackFailed && <p role="alert" className="paywall-screen__subtitle">La connexion RDVPermis a été interrompue. Vous pouvez la relancer depuis les réglages.</p>}
        {rdvReconnectRequired && (
          <div
            style={{
              background: '#FEF2F2',
              borderBottom: '1px solid #F87171',
              padding: '0.6rem 1.5rem',
              fontSize: '0.875rem',
              color: '#991B1B',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}
          >
            <span>⚠</span>
            <span>
              Votre connexion RdvPermis a expiré — l'auto-réservation est suspendue.{' '}
            </span>
            <button
              onClick={() => reconnectMutation.mutate()}
              disabled={reconnectMutation.isPending}
              style={{
                background: '#DC2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 12px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {reconnectMutation.isPending ? 'Chargement…' : 'Reconnecter'}
            </button>
          </div>
        )}
        {showPastDueBanner && (
          <div
            style={{
              background: '#FEF3C7',
              borderBottom: '1px solid #F59E0B',
              padding: '0.6rem 1.5rem',
              fontSize: '0.875rem',
              color: '#92400E',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span>⚠️</span>
            <span>
              Votre dernier paiement a échoué. Mettez à jour votre moyen de paiement
              pour éviter une interruption de service.{' '}
              <a href="/reglages/abonnement" style={{ fontWeight: 600, textDecoration: 'underline' }}>
                Gérer l&rsquo;abonnement
              </a>
            </span>
          </div>
        )}
        {/* F46 (suite) : fiche de l'école incomplète, blocage de l'émission annoncé. */}
        <LegalProfileBanner />
        <main className={isV2Page ? 'v2-root flex-1 overflow-y-auto' : 'app-shell__main'}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default ProtectedLayout;
